import React, { useCallback, useRef, forwardRef, KeyboardEvent } from "react";
import FocusLock from "react-focus-lock";
import { RemoveScroll } from "react-remove-scroll";

import classnames from "classnames";

import { StyledWindow } from "./Style";
import { Portal, useLayerContext } from "../../helpers";
import { layers } from "../../utils";
import useForkRef from "../../utils/useForkRef";

export interface IWindow {
  onClose?: () => void;
  disableFocusLock?: boolean;
  disableScrollLock?: boolean;
  disableCloseOnEsc?: boolean;
}

const returnFocus = { preventScroll: true };

export const Window = forwardRef<
  HTMLDivElement,
  IWindow & Omit<React.ComponentPropsWithoutRef<"div">, keyof IWindow>
>(
  (
    {
      children,
      onClose,
      className,
      style,
      onKeyDown,
      disableFocusLock = false,
      disableScrollLock = false,
      disableCloseOnEsc = false,
      onClick,
      ...rest
    },
    ref
  ) => {
    const innerRef = useRef<HTMLDivElement>(null);
    const handleRef = useForkRef(innerRef, ref);
    const { zIndex = layers.skyscraper } = useLayerContext();

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        if (
          !event.defaultPrevented &&
          (event.key === "Esc" || event.key === "Escape") &&
          !disableCloseOnEsc
        ) {
          event.preventDefault();
          if (onClose) {
            onClose();
          }
        }
        if (typeof onKeyDown === "function") {
          onKeyDown(event);
        }
      },
      [disableCloseOnEsc, onClose, onKeyDown]
    );

    const handleClick = useCallback(
      (event) => {
        event?.stopPropagation();
        typeof onClick === "function" && onClick(event);
      },
      [onClick]
    );

    return (
      <Portal>
        <FocusLock returnFocus={returnFocus} disabled={disableFocusLock}>
          <RemoveScroll
            forwardProps
            ref={handleRef}
            enabled={!disableScrollLock}
            noIsolation
          >
            <StyledWindow
              {...rest}
              style={{ zIndex, ...style }}
              onKeyDown={handleKeyDown}
              tabIndex={0}
              className={classnames("c-window", className)}
              onClick={handleClick}
            >
              {children}
            </StyledWindow>
          </RemoveScroll>
        </FocusLock>
      </Portal>
    );
  }
);

Window.displayName = "Window";
